import { CASE_TYPE, CREATE_REQUEST_STATUS, LIFECARE_TAB_TYPE_TO_COMMENT_FIELD, LIFECARE_TABS, REQUEST_TYPES, SECTION_UI_STATE, SUGGESTED_EDIT_SECTIONS } from "./enum.js";
import { isValidObjectId } from "./utils.js";

export const normalizeString = (value) => (typeof value === "string" ? value.trim() : "");

const FACTS_OF_INCIDENT_LABEL = "Facts of the Incident";
const DANGEROUS_CONDITION_LABEL = "Dangerous Condition";
const ACTUAL_CONSTRUCTIVE_NOTICE_LABEL = "Actual Constructive Notice";

const TAB_LABELS_BY_TYPE = {
  [SUGGESTED_EDIT_SECTIONS.EXECUTIVE_SUMMARY]: "Executive Summary",
  [SUGGESTED_EDIT_SECTIONS.LIABILITY_SINGLE]: "Liability",
  [SUGGESTED_EDIT_SECTIONS.LIABILITY_PER_DEFENDANT]: "Liability",
  [SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_SINGLE]: FACTS_OF_INCIDENT_LABEL,
  [SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_PER_DEFENDANT]: FACTS_OF_INCIDENT_LABEL,
  [SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_SINGLE]: DANGEROUS_CONDITION_LABEL,
  [SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_PER_DEFENDANT]: DANGEROUS_CONDITION_LABEL,
  [SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_SINGLE]: ACTUAL_CONSTRUCTIVE_NOTICE_LABEL,
  [SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_PER_DEFENDANT]: ACTUAL_CONSTRUCTIVE_NOTICE_LABEL,
  [SUGGESTED_EDIT_SECTIONS.MEDICAL_TREATMENT]: "Medical Treatment",
  [SUGGESTED_EDIT_SECTIONS.PRIOR_MEDICAL]: "Prior Medical",
  [SUGGESTED_EDIT_SECTIONS.LIFECARE]: "Lifecare Plan",
  [SUGGESTED_EDIT_SECTIONS.PAIN_AND_SUFFERING]: "Pain and Suffering",
  ...Object.fromEntries(
      LIFECARE_TABS.map((tab) => [tab.tabType, tab.label])
    ),
};

function getSnapshotDataFromRequest(request) {
  return request?.snapshot?.caseData || {};
}

export const getTabLabel = (section) => TAB_LABELS_BY_TYPE[section?.type] || section?.label || "";
export const SECTION_STATUS_POLL_MS = 30000;
// tighter cadence for gating studio exit / downloads on demand letter regeneration
export const CASE_REGENERATION_POLL_MS = 2500;

const getClientDisplayName = (client, index) =>
  client?.personalInfo?.clientName || `Plaintiff ${index + 1}`;

const buildClientNameMap = (clients = []) =>
  new Map(
    clients
      .filter((client) => client?.clientId)
      .map((client, index) => [client.clientId, getClientDisplayName(client, index)])
  );

const getExecutiveSummaryId = () => {
  return SUGGESTED_EDIT_SECTIONS.EXECUTIVE_SUMMARY;
};

const getSingleLiabilityId = () => {
  return SUGGESTED_EDIT_SECTIONS.LIABILITY_SINGLE;
};

const getLiabilityPerDefendantId = (defendantId) => {
  return `${SUGGESTED_EDIT_SECTIONS.LIABILITY_PER_DEFENDANT}-${defendantId}`;
};

// Each liability field maps a display label to its single (case-level) and per-defendant section types.
const LIABILITY_FIELD_POLICE_REPORT = {
  label: "Liability",
  singleType: SUGGESTED_EDIT_SECTIONS.LIABILITY_SINGLE,
  perDefendantType: SUGGESTED_EDIT_SECTIONS.LIABILITY_PER_DEFENDANT,
};
const LIABILITY_FIELD_FACTS_OF_INCIDENT = {
  label: FACTS_OF_INCIDENT_LABEL,
  singleType: SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_SINGLE,
  perDefendantType: SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_PER_DEFENDANT,
};
const LIABILITY_FIELD_DANGEROUS_CONDITION = {
  label: DANGEROUS_CONDITION_LABEL,
  singleType: SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_SINGLE,
  perDefendantType: SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_PER_DEFENDANT,
};
const LIABILITY_FIELD_ACTUAL_CONSTRUCTIVE_NOTICE = {
  label: ACTUAL_CONSTRUCTIVE_NOTICE_LABEL,
  singleType: SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_SINGLE,
  perDefendantType: SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_PER_DEFENDANT,
};

const PREMISES_LIABILITY_FIELDS = [
  LIABILITY_FIELD_FACTS_OF_INCIDENT,
  LIABILITY_FIELD_DANGEROUS_CONDITION,
  LIABILITY_FIELD_ACTUAL_CONSTRUCTIVE_NOTICE,
];

const LIABILITY_FIELDS_BY_CASE_TYPE = {
  [CASE_TYPE.AUTO_ACCIDENT]: [LIABILITY_FIELD_POLICE_REPORT],
  [CASE_TYPE.TRIP_AND_FALL]: PREMISES_LIABILITY_FIELDS,
  [CASE_TYPE.SLIP_AND_FALL]: PREMISES_LIABILITY_FIELDS,
  [CASE_TYPE.PREMISE_LIABILITY]: PREMISES_LIABILITY_FIELDS,
  // Dog bite has no dangerous condition or notice inputs, so it only surfaces facts of the incident.
  [CASE_TYPE.DOG_BITE]: [LIABILITY_FIELD_FACTS_OF_INCIDENT],
};

// Falls back to the police-report liability field for unknown/legacy case types.
const getLiabilityFieldsForCaseType = (caseType) =>
  LIABILITY_FIELDS_BY_CASE_TYPE[caseType] || [LIABILITY_FIELD_POLICE_REPORT];

const buildLiabilitySections = ({ caseType, isSingleLiabilitySectionForAllDefendants, defendants = [] }) => {
  const liabilityFields = getLiabilityFieldsForCaseType(caseType);
  const sections = [];
  liabilityFields.forEach((field) => {
    if (isSingleLiabilitySectionForAllDefendants) {
      sections.push({
        id: field.singleType,
        label: field.label,
        type: field.singleType,
        targetId: null,
      });
      return;
    }
    defendants.forEach((defendant, index) => {
      if (!defendant?.defendantId) return;
      const defendantName = defendant?.personalInfo?.name || `Defendant ${index + 1}`;
      sections.push({
        id: `${field.perDefendantType}-${defendant.defendantId}`,
        label: `${defendantName} ${field.label}`,
        type: field.perDefendantType,
        targetId: defendant.defendantId,
      });
    });
  });
  return sections;
};

const getMedicalTreatmentId = (clientId) => {
  return `${SUGGESTED_EDIT_SECTIONS.MEDICAL_TREATMENT}-${clientId}`;
};

const getPriorMedicalId = (clientId) => {
  return `${SUGGESTED_EDIT_SECTIONS.PRIOR_MEDICAL}-${clientId}`;
};

const getLifecareId = (clientId) => {
  return `${SUGGESTED_EDIT_SECTIONS.LIFECARE}-${clientId}`;
};

const getPainAndSufferingId = (clientId) => {
  return `${SUGGESTED_EDIT_SECTIONS.PAIN_AND_SUFFERING}-${clientId}`;
};

const getMedicalTreatmentByClientFromRequest = (request, clientId) => {
  const snapshotData = getSnapshotDataFromRequest(request);
  const client = (snapshotData?.clients || []).find((c) => c.clientId === clientId);
  return client?.medicalProviders?.postAccident?.result?.medicalRecordsParagraphs;
}

const getPriorMedicalTreatmentByClientFromRequest = (request, clientId) => {
  const snapshotData = getSnapshotDataFromRequest(request);
  const client = (snapshotData?.clients || []).find((c) => c.clientId === clientId);
  return client?.medicalProviders?.preAccident?.result?.medicalRecordsParagraphs;
}

const getLifecareByClientFromRequest = (request, clientId) => {
  const snapshotData = getSnapshotDataFromRequest(request);
  const client = (snapshotData?.clients || []).find((c) => c.clientId === clientId);
  return client?.medicalProviders?.postAccident?.result?.lifecarePlan;
}

const getPainAndSufferingByClientFromRequest = (request, clientId) => {
  const snapshotData = getSnapshotDataFromRequest(request);
  const client = (snapshotData?.clients || []).find((c) => c.clientId === clientId);
  return client?.painAndSuffering?.result?.painAndSufferingReport;
}

const getMedicalBillRecordsByClientFromRequest = (request, clientId) => {
  const snapshotData = getSnapshotDataFromRequest(request);
  const client = (snapshotData?.clients || []).find((c) => c.clientId === clientId);
  const records = client?.medicalProviders?.postAccident?.result?.medicalBillRecords;
  return Array.isArray(records) ? records : [];
}

const getLifecareStructuredByClientFromRequest = (request, clientId) => {
  const snapshotData = getSnapshotDataFromRequest(request);
  const client = (snapshotData?.clients || []).find((c) => c.clientId === clientId);
  console.log("lifecare structured ", client?.medicalProviders?.postAccident?.result?.lifecarePlanStructured)
  return client?.medicalProviders?.postAccident?.result?.lifecarePlanStructured || null;
}

export const getLifecareStructuredFromResultRequests = (resultRequests, clientId) => {
  let plan = null;
  for (const request of resultRequests || []) {
    const candidate = getLifecareStructuredByClientFromRequest(request, clientId);
    if (candidate) plan = candidate;
  }
  return plan;
}

export const SECTION_ID_GETTERS = {
  [SUGGESTED_EDIT_SECTIONS.EXECUTIVE_SUMMARY]: getExecutiveSummaryId,
  [SUGGESTED_EDIT_SECTIONS.LIABILITY_SINGLE]: getSingleLiabilityId,
  [SUGGESTED_EDIT_SECTIONS.LIABILITY_PER_DEFENDANT]: getLiabilityPerDefendantId,
  [SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_SINGLE]: () => SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_SINGLE,
  [SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_PER_DEFENDANT]: (defendantId) => `${SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_PER_DEFENDANT}-${defendantId}`,
  [SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_SINGLE]: () => SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_SINGLE,
  [SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_PER_DEFENDANT]: (defendantId) => `${SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_PER_DEFENDANT}-${defendantId}`,
  [SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_SINGLE]: () => SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_SINGLE,
  [SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_PER_DEFENDANT]: (defendantId) => `${SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_PER_DEFENDANT}-${defendantId}`,
  [SUGGESTED_EDIT_SECTIONS.MEDICAL_TREATMENT]: getMedicalTreatmentId,
  [SUGGESTED_EDIT_SECTIONS.PRIOR_MEDICAL]: getPriorMedicalId,
  [SUGGESTED_EDIT_SECTIONS.LIFECARE]: getLifecareId,
  [SUGGESTED_EDIT_SECTIONS.PAIN_AND_SUFFERING]: getPainAndSufferingId,
}

export const buildSectionDefinitions = (caseDetails, resultRequests) => {
  const defendants = caseDetails?.defendants || [];
  const clients = caseDetails?.clients || [];
  const priorMedicalClients = getPriorMedicalClients(resultRequests);
  const isSingleLiabilitySectionForAllDefendants = Boolean(caseDetails?.caseInfo?.isSingleLiabilitySectionForAllDefendants);
  const caseType = caseDetails?.caseInfo?.caseType;
  const clientNameMap = buildClientNameMap(clients);
  const sections = [
    {
      id: getExecutiveSummaryId(),
      label: "Executive Summary",
      type: SUGGESTED_EDIT_SECTIONS.EXECUTIVE_SUMMARY,
      targetId: null,
    },
  ];

  sections.push(...buildLiabilitySections({
    caseType,
    isSingleLiabilitySectionForAllDefendants,
    defendants,
  }));

  clients.forEach((client, index) => {
    if (!client?.clientId) return;
    const clientLabel = clientNameMap.get(client.clientId) || getClientDisplayName(client, index);
    sections.push({
      id: getMedicalTreatmentId(client.clientId),
      label: `${clientLabel} Medical treatment`,
      type: SUGGESTED_EDIT_SECTIONS.MEDICAL_TREATMENT,
      targetId: client.clientId,
    });
  });

  priorMedicalClients.forEach((client) => {
    if (!client?.clientId) return;
    const clientLabel = clientNameMap.get(client.clientId) || "Plaintiff";
    sections.push({
      id: getPriorMedicalId(client.clientId),
      label: `${clientLabel} Prior medical`,
      type: SUGGESTED_EDIT_SECTIONS.PRIOR_MEDICAL,
      targetId: client.clientId,
    });
  });

  clients.forEach((client, index) => {
    if (!client?.clientId) return;
    const clientLabel = clientNameMap.get(client.clientId) || getClientDisplayName(client, index);
    sections.push({
      id: getPainAndSufferingId(client.clientId),
      label: `${clientLabel} Pain and Suffering`,
      type: SUGGESTED_EDIT_SECTIONS.PAIN_AND_SUFFERING,
      targetId: client.clientId,
    });
  });

  return sections;
};

// One section definition per (lifecare tab) x (lifecare client). Used exclusively by the
// Lifecare Studio. Definitions cover EVERY lifecare client (not just the selected one) so the
// case-level editComments array round-trips all clients on save -- the studio only displays the
// active client's slice. The SUMMARY tab is intentionally NOT a section (no comment field; totals
// are server-derived) -- it shows up only as a tab in the tab-state hook.
export const buildLifecareSectionDefinitions = ({ clientIds = [] } = {}) => {
  const ids = (Array.isArray(clientIds) ? clientIds : []).filter(Boolean);
  if (!ids.length) return [];
  const commentTabs = LIFECARE_TABS.filter((tab) => Boolean(tab.commentField));
  // one row per comment-bearing lifecare tab per client; the model picks the edit tool
  return ids.flatMap((clientId) =>
    commentTabs.map((tab) => ({
      id: `${tab.tabType}-${clientId}`,
      label: tab.label,
      type: tab.tabType,
      targetId: clientId,
    }))
  );
};

const matchesSectionComment = (comment, section) => {
  if (!comment) return false;
  switch (section.type) {
    case SUGGESTED_EDIT_SECTIONS.EXECUTIVE_SUMMARY:
      return Boolean(comment?.executiveSummary);
    case SUGGESTED_EDIT_SECTIONS.LIABILITY_SINGLE:
      return Boolean(comment?.liabilitySingle);
    case SUGGESTED_EDIT_SECTIONS.LIABILITY_PER_DEFENDANT:
      return Boolean(comment?.liabilityPerDefendantByDefendant?.[section.targetId]);
    case SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_SINGLE:
      return Boolean(comment?.factsOfIncidentSingle);
    case SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_PER_DEFENDANT:
      return Boolean(comment?.factsOfIncidentPerDefendantByDefendant?.[section.targetId]);
    case SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_SINGLE:
      return Boolean(comment?.dangerousConditionSingle);
    case SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_PER_DEFENDANT:
      return Boolean(comment?.dangerousConditionPerDefendantByDefendant?.[section.targetId]);
    case SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_SINGLE:
      return Boolean(comment?.actualConstructiveNoticeSingle);
    case SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_PER_DEFENDANT:
      return Boolean(comment?.actualConstructiveNoticePerDefendantByDefendant?.[section.targetId]);
    case SUGGESTED_EDIT_SECTIONS.MEDICAL_TREATMENT:
      return Boolean(comment?.medicalTreatmentByClient?.[section.targetId]);
    case SUGGESTED_EDIT_SECTIONS.PRIOR_MEDICAL:
      return Boolean(comment?.priorMedicalByClient?.[section.targetId]);
    case SUGGESTED_EDIT_SECTIONS.LIFECARE:
      return Boolean(comment?.lifecareByClient?.[section.targetId]);
    case SUGGESTED_EDIT_SECTIONS.PAIN_AND_SUFFERING:
      return Boolean(comment?.painAndSufferingByClient?.[section.targetId]);
    default:{
      const lifecareCommentField = LIFECARE_TAB_TYPE_TO_COMMENT_FIELD[section.type];
      if (lifecareCommentField) {
        return Boolean(comment?.[lifecareCommentField]?.[section.targetId]);
      }
      return false;
    }
  }
};

export const buildSectionComments = (sectionDefinitions = [], editComments = []) => {
  const usedIds = new Set();
  const now = new Date().toISOString();
  return sectionDefinitions.map((section) => {
    const match = (editComments || []).find((comment) => {
      const isMatch = matchesSectionComment(comment, section);
      if (!isMatch) return false;
      if (!comment?._id) return true;
      return !usedIds.has(comment._id);
    });
    if (match?._id) {
      usedIds.add(match._id);
    }
    return {
      ...section,
      commentId: match?._id,
      createdAt: match?.createdAt || match?.updatedAt || now,
      lastEditedAt: match?.lastEditedAt || match?.updatedAt || match?.createdAt || null,
      comment: match?.comment || "",
    };
  });
};

export const buildCommentPayload = ({ section, now }) => {
  const base = {
    createdAt: section.createdAt || now,
    lastEditedAt: now,
    executiveSummary: false,
    liabilitySingle: false,
    factsOfIncidentSingle: false,
    dangerousConditionSingle: false,
    actualConstructiveNoticeSingle: false,
    medicalTreatmentByClient: {},
    priorMedicalByClient: {},
    lifecareByClient: {},
    painAndSufferingByClient: {},
    liabilityPerDefendantByDefendant: {},
    factsOfIncidentPerDefendantByDefendant: {},
    dangerousConditionPerDefendantByDefendant: {},
    actualConstructiveNoticePerDefendantByDefendant: {},
    lifecareInfoByClient: {},
    lifecareDiagnosisByClient: {},
    lifecareLifeExpectancyByClient: {},
    lifecareSummaryOfRecordsByClient: {},
    lifecareFutureCareNeedsByClient: {},
    comment: normalizeString(section.comment),
  };

  switch (section.type) {
    case SUGGESTED_EDIT_SECTIONS.EXECUTIVE_SUMMARY:
      base.executiveSummary = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.LIABILITY_SINGLE:
      base.liabilitySingle = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.LIABILITY_PER_DEFENDANT:
      base.liabilityPerDefendantByDefendant[section.targetId] = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_SINGLE:
      base.factsOfIncidentSingle = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_PER_DEFENDANT:
      base.factsOfIncidentPerDefendantByDefendant[section.targetId] = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_SINGLE:
      base.dangerousConditionSingle = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_PER_DEFENDANT:
      base.dangerousConditionPerDefendantByDefendant[section.targetId] = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_SINGLE:
      base.actualConstructiveNoticeSingle = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_PER_DEFENDANT:
      base.actualConstructiveNoticePerDefendantByDefendant[section.targetId] = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.MEDICAL_TREATMENT:
      base.medicalTreatmentByClient[section.targetId] = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.PRIOR_MEDICAL:
      base.priorMedicalByClient[section.targetId] = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.LIFECARE:
      base.lifecareByClient[section.targetId] = true;
      break;
    case SUGGESTED_EDIT_SECTIONS.PAIN_AND_SUFFERING:
      base.painAndSufferingByClient[section.targetId] = true;
      break;
    default: {
      const lifecareCommentField = LIFECARE_TAB_TYPE_TO_COMMENT_FIELD[section.type];
      if (lifecareCommentField) {
        base[lifecareCommentField][section.targetId] = true;
      }
      break;
    }
  }

  if (section.commentId && isValidObjectId(section.commentId)) {
    base._id = section.commentId;
  }
  console.log("For section ", section, " built payload ", base);
  return base;
};

export const getCommentIdForSection = ({ sectionId, sectionDefinitions = [], editComments = [] }) => {
  if (!sectionId) return null;
  const targetSection = sectionDefinitions.find((section) => section.id === sectionId);
  if (!targetSection) return null;
  const match = (editComments || []).find((comment) => matchesSectionComment(comment, targetSection));
  return match?._id || null;
};

export const buildSuggestedEditPayload = ({ caseId, commentIds }) => {
  const normalizedCommentIds = Array.isArray(commentIds) ? commentIds.filter(Boolean) : [];
  const payload = { caseId, requestType: REQUEST_TYPES.SUGGESTED_EDIT };
  if (normalizedCommentIds.length) payload.commentIds = normalizedCommentIds;
  return payload;
};

export const mapCreateRequestStatusToUiState = (status) => {
  if ([CREATE_REQUEST_STATUS.PENDING, CREATE_REQUEST_STATUS.DEFERRED, CREATE_REQUEST_STATUS.IN_PROGRESS].includes(status)) {
    return SECTION_UI_STATE.LOADING;
  }
  if (status === CREATE_REQUEST_STATUS.COMPLETED) {
    return SECTION_UI_STATE.SUCCESS;
  }
  if ([CREATE_REQUEST_STATUS.FAILED, CREATE_REQUEST_STATUS.BAD_RECORD].includes(status)) {
    return SECTION_UI_STATE.FAILED;
  }
  return SECTION_UI_STATE.IDLE;
};

export const buildEditCommentsBuckets = ({ sections = [], now }) => {
  const editComments = [];
  const deletedEditComments = [];

  sections.forEach((section) => {
    const hasComment = Boolean(normalizeString(section.comment));
    if (hasComment) {
      editComments.push(buildCommentPayload({ section, now }));
      return;
    }
    if (section.commentId && isValidObjectId(section.commentId)) {
      deletedEditComments.push({
        ...buildCommentPayload({ section, now }),
        deletedAt: now,
      });
    }
  });

  return { editComments, deletedEditComments };
};

export const buildEditCommentsRequestPayload = ({ caseId, sections = [] }) => {
  const now = new Date().toISOString();
  const { editComments, deletedEditComments } = buildEditCommentsBuckets({ sections, now });
  return JSON.stringify({ caseId, editComments, deletedEditComments });
};

export const getUpdatedEditCommentsFromResponse = (response) => {
  if (!Array.isArray(response?.data?.editComments)) {
    return null;
  }
  return response.data.editComments;
};

const getPriorMedicalClients = (resultRequests) => {
  return getFilteredClientsFromResultRequests(
    resultRequests,
    (client) => client?.medicalProviders?.preAccident?.medicalRecords?.length
  );
}

const getFilteredClientsFromResultRequests = (resultRequests, filterFn) => {
  if (resultRequests?.length) {
    const demandRequest = resultRequests[0];
    const snapshotData = getSnapshotDataFromRequest(demandRequest);
    return snapshotData?.clients?.filter(filterFn) || [];
  } else {
    return [];
  }
}

const getExecutiveSummaryFromRequest = (request) => {
  const snapshotData = getSnapshotDataFromRequest(request);
  return snapshotData?.result?.executiveSummary;
};

// Case-level liability result field getter (single-liability mode).
const getCaseResultFieldFromRequest = (request, fieldName) => {
  const snapshotData = getSnapshotDataFromRequest(request);
  return snapshotData?.result?.[fieldName];
};

// Per-defendant liability result field getter (per-defendant mode).
const getDefendantResultFieldFromRequest = (request, defendantId, fieldName) => {
  const snapshotData = getSnapshotDataFromRequest(request);
  const defendant = (snapshotData?.defendants || []).find(
    (d) => d?.defendantId === defendantId
  );
  return defendant?.result?.[fieldName];
};

const getSingleLiabilityFromRequest = (request) => getCaseResultFieldFromRequest(request, "policeReportResponse");
const getLiabilityPerDefendantFromRequest = (request, defendantId) => getDefendantResultFieldFromRequest(request, defendantId, "policeReportResponse");
const getFactsOfIncidentSingleFromRequest = (request) => getCaseResultFieldFromRequest(request, "factsOfIncident");
const getFactsOfIncidentPerDefendantFromRequest = (request, defendantId) => getDefendantResultFieldFromRequest(request, defendantId, "factsOfIncidentLLMResponse");
const getDangerousConditionSingleFromRequest = (request) => getCaseResultFieldFromRequest(request, "dangerousCondition");
const getDangerousConditionPerDefendantFromRequest = (request, defendantId) => getDefendantResultFieldFromRequest(request, defendantId, "dangerousConditionLLMResponse");
const getActualConstructiveNoticeSingleFromRequest = (request) => getCaseResultFieldFromRequest(request, "actualConstructedNotice");
const getActualConstructiveNoticePerDefendantFromRequest = (request, defendantId) => getDefendantResultFieldFromRequest(request, defendantId, "actualConstructedNoticeLLMResponse");

const SECTION_GETTERS = {
  [SUGGESTED_EDIT_SECTIONS.EXECUTIVE_SUMMARY]: getExecutiveSummaryFromRequest,
  [SUGGESTED_EDIT_SECTIONS.LIABILITY_SINGLE]: getSingleLiabilityFromRequest,
  [SUGGESTED_EDIT_SECTIONS.LIABILITY_PER_DEFENDANT]: getLiabilityPerDefendantFromRequest,
  [SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_SINGLE]: getFactsOfIncidentSingleFromRequest,
  [SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_PER_DEFENDANT]: getFactsOfIncidentPerDefendantFromRequest,
  [SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_SINGLE]: getDangerousConditionSingleFromRequest,
  [SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_PER_DEFENDANT]: getDangerousConditionPerDefendantFromRequest,
  [SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_SINGLE]: getActualConstructiveNoticeSingleFromRequest,
  [SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_PER_DEFENDANT]: getActualConstructiveNoticePerDefendantFromRequest,
  [SUGGESTED_EDIT_SECTIONS.MEDICAL_TREATMENT]: getMedicalTreatmentByClientFromRequest,
  [SUGGESTED_EDIT_SECTIONS.PRIOR_MEDICAL]: getPriorMedicalTreatmentByClientFromRequest,
  [SUGGESTED_EDIT_SECTIONS.LIFECARE]: getLifecareByClientFromRequest,
  [SUGGESTED_EDIT_SECTIONS.PAIN_AND_SUFFERING]: getPainAndSufferingByClientFromRequest,
}

const hasSectionValue = (value) => value !== undefined && value !== null;

// TODO adjust this logic for undo/redo stack
const getSectionFromResultRequestsDecorator = (resultRequests, getter) => {
  return (id) => {
    let sectionText = "";
    // the newest snapshot that carries the field wins, including one the user cleared to ""
    for (const request of resultRequests || []) {
      const requestSectionText = getter(request, id);
      if (!hasSectionValue(requestSectionText)) continue;
      sectionText = requestSectionText;
    }
    return sectionText;
  };
};

export const getSectionTextFromResultRequests = (resultRequests, section) => {
  const getter = SECTION_GETTERS[section?.type];
  // lifecare studio feeds its own section types through here and they have no demand getter
  if (!getter) return "";
  const decoratedGetter = getSectionFromResultRequestsDecorator(resultRequests, getter);
  return decoratedGetter(section.targetId);
}

// A section is applicable when its field exists in any snapshot, even blank. Testing for non-blank
// text instead would hide a section the user cleared, and a hidden section renders no textarea to
// type the content back into.
export const isSectionPresentInResultRequests = (resultRequests, section) => {
  const getter = SECTION_GETTERS[section?.type];
  if (!getter) return false;
  return (resultRequests || []).some((request) => hasSectionValue(getter(request, section.targetId)));
}

const parseMedicalBillAmount = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  const normalizedValue = String(value ?? "").replace(/[^0-9.-]/g, "");
  if (!normalizedValue) {
    return 0;
  }
  const parsed = Number(normalizedValue);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getProviderNameForBillRecord = (record = {}) =>
  record?.medicalProviderName || record?.providerName || "";

export const getDefaultMedicalProviderName = ({ caseDetails, clientId, existingRows = [] }) => {
  const firstExistingName = getProviderNameForBillRecord(existingRows[0]);
  if (firstExistingName) {
    return firstExistingName;
  }
  const client = (caseDetails?.clients || []).find((item) => item?.clientId === clientId);
  const providerName = client?.medicalProviders?.postAccident?.medicalRecords?.[0]?.providerName || "";
  return providerName;
};

const groupMedicalBillRecordsByProviderName = (records = []) => {
  const groupedRecordsMap = new Map();
  records.forEach((record) => {
    const providerName = String(getProviderNameForBillRecord(record) || "").trim();
    const providerKey = providerName.toLowerCase();
    const existing = groupedRecordsMap.get(providerKey);
    const nextTotalBillAmount =
      parseMedicalBillAmount(existing?.totalBillAmount) +
      parseMedicalBillAmount(record?.totalBillAmount);
    groupedRecordsMap.set(providerKey, {
      ...(existing || {}),
      ...record,
      medicalProviderName: providerName,
      totalBillAmount: nextTotalBillAmount,
    });
  });
  return Array.from(groupedRecordsMap.values());
};

export const getMedicalBillRecordsByClient = ({ resultRequests, sectionDefinitions = [] }) => {
  const next = {};
  sectionDefinitions.forEach((section) => {
    if (section.type !== SUGGESTED_EDIT_SECTIONS.MEDICAL_TREATMENT || !section.targetId) return;
    const clientBillRecordsGetter = getSectionFromResultRequestsDecorator(
      resultRequests,
      getMedicalBillRecordsByClientFromRequest
    );
    const clientBillRecords = clientBillRecordsGetter(section.targetId);
    next[section.targetId] = groupMedicalBillRecordsByProviderName(clientBillRecords);
  });
  return next;
};

export const getSectionUpdatePathFromId = (sectionId) => {
  const idParts = String(sectionId || "").split("-");
  const type = idParts[0];
  const targetId = idParts.slice(1).join("-");
  switch (type) {
    case SUGGESTED_EDIT_SECTIONS.EXECUTIVE_SUMMARY:
      return "result.executiveSummary";
    case SUGGESTED_EDIT_SECTIONS.LIABILITY_SINGLE:
      return "result.policeReportResponse";
    case SUGGESTED_EDIT_SECTIONS.LIABILITY_PER_DEFENDANT:
      return `defendants.{defendantId:${targetId}}.result.policeReportResponse`;
    case SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_SINGLE:
      return "result.factsOfIncident";
    case SUGGESTED_EDIT_SECTIONS.FACTS_OF_INCIDENT_PER_DEFENDANT:
      return `defendants.{defendantId:${targetId}}.result.factsOfIncidentLLMResponse`;
    case SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_SINGLE:
      return "result.dangerousCondition";
    case SUGGESTED_EDIT_SECTIONS.DANGEROUS_CONDITION_PER_DEFENDANT:
      return `defendants.{defendantId:${targetId}}.result.dangerousConditionLLMResponse`;
    case SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_SINGLE:
      return "result.actualConstructedNotice";
    case SUGGESTED_EDIT_SECTIONS.ACTUAL_CONSTRUCTIVE_NOTICE_PER_DEFENDANT:
      return `defendants.{defendantId:${targetId}}.result.actualConstructedNoticeLLMResponse`;
    case SUGGESTED_EDIT_SECTIONS.MEDICAL_TREATMENT:
      return `clients.{clientId:${targetId}}.medicalProviders.postAccident.result.medicalRecordsParagraphs`;
    case SUGGESTED_EDIT_SECTIONS.PRIOR_MEDICAL:
      return `clients.{clientId:${targetId}}.medicalProviders.preAccident.result.medicalRecordsParagraphs`;
    case SUGGESTED_EDIT_SECTIONS.LIFECARE:
      return `clients.{clientId:${targetId}}.medicalProviders.postAccident.result.lifecarePlan`;
    case SUGGESTED_EDIT_SECTIONS.PAIN_AND_SUFFERING:
      return `clients.{clientId:${targetId}}.painAndSuffering.result.painAndSufferingReport`;
    default:
      return null;
  }
};

const normalizeMedicalBillAmountForStorage = (value) => {
  const normalizedValue = String(value ?? "").replace(/[^0-9.-]/g, "");
  if (!normalizedValue) {
    return "";
  }
  const parsed = Number(normalizedValue);
  return Number.isFinite(parsed) ? String(parsed) : "";
};

export const getMedicalBillRecordsUpdatePath = (clientId) =>
  `clients.{clientId:${clientId}}.medicalProviders.postAccident.result.medicalBillRecords`;

export const buildMedicalBillRecordsForStorage = (rows = []) =>
  groupMedicalBillRecordsByProviderName(rows).map((row) => ({
    ...row,
    medicalProviderName: String(getProviderNameForBillRecord(row) || "").trim(),
    totalBillAmount: normalizeMedicalBillAmountForStorage(row?.totalBillAmount),
  }));